// apps/web/src/layouts/components/SidebarVehicleQueueBadge.tsx
//
// Badge dinâmica da fila de classificação FIPE (PRECIFICAÇÃO > Catálogo de
// veículos). Espelha o <SidebarSupportBadge>: conta os modelos importados sem
// subgrupo/classificação aguardando o operador. O conteúdo vive num componente
// interno renderizado só quando o usuário tem `pricing:manage` — assim a query
// nem monta para quem não pode classificar.
// Contrato: docs/contracts/ESCR-fipe-import-classificacao.md.

import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useSocketEvent, useVehicleQueueCount, vehicleCatalogKeys } from '@alamo/api';
import { usePermissions } from '@alamo/auth';

export interface SidebarVehicleQueueBadgeProps {
  variant?: 'highlighted' | 'muted';
  ariaLabel?: string;
}

export function SidebarVehicleQueueBadge({ variant, ariaLabel }: SidebarVehicleQueueBadgeProps) {
  const { hasPermission } = usePermissions();
  if (!hasPermission('pricing', 'manage')) return null;
  return <SidebarVehicleQueueBadgeInner variant={variant} ariaLabel={ariaLabel} />;
}

function SidebarVehicleQueueBadgeInner({
  variant = 'highlighted',
  ariaLabel,
}: SidebarVehicleQueueBadgeProps) {
  const { t } = useTranslation('admin_pricing');
  const queryClient = useQueryClient();

  // Realtime: o backend emite `vehicle-catalog.queue.count_changed` (room
  // `admin:vehicle-catalog-queue`, auto-join) ao fim de um sync FIPE que
  // importou modelos sem subgrupo. Recontamos server-side via /queue/count —
  // nunca no front. useSocketEvent adquire o socket compartilhado, então a
  // assinatura mantém a conexão viva enquanto o badge está montado.
  useSocketEvent('vehicle-catalog.queue.count_changed', () => {
    void queryClient.invalidateQueries({ queryKey: vehicleCatalogKeys.queueCount() });
  });

  const { data } = useVehicleQueueCount();

  const total = data?.count ?? 0;
  if (total === 0) return null;

  const style =
    variant === 'highlighted' ? 'bg-amber-500 text-white' : 'bg-amber-100 text-amber-800';
  const label = ariaLabel ?? t('vehicle_queue.badge_aria', { count: total });

  return (
    <span
      data-testid="sidebar-vehicle-queue-badge"
      title={label}
      aria-label={label}
      className={
        'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 ' +
        'text-[10px] font-body font-semibold tabular-nums ' +
        style
      }
    >
      {total > 99 ? '99+' : total}
    </span>
  );
}
